import { useEffect, useState } from 'react';
import type { CrewMember } from '../state/session';
import { colors } from '../theme';

type LocRow = {
  user_id: string;
  lat: number;
  lng: number;
  updated_at: string;
  status: string | null;
};

type PresenceState = 'fresh' | 'stale' | 'offline' | 'no_signal' | 'sharing_off';

const STALE_MS = 10 * 60 * 1000;

function presenceFor(loc: LocRow | undefined, share_location: boolean): PresenceState {
  if (!loc) return share_location ? 'no_signal' : 'sharing_off';
  if (loc.status === 'offline') return 'offline';
  const ageMs = Date.now() - new Date(loc.updated_at).getTime();
  return ageMs > STALE_MS ? 'stale' : 'fresh';
}

function stalenessLabel(iso: string): string {
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 30) return 'now';
  if (secs < 90) return '1m ago';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ago`;
}

const presenceOrder: Record<PresenceState, number> = {
  fresh: 0,
  stale: 1,
  offline: 2,
  sharing_off: 3,
  no_signal: 4,
};

type Props = {
  crew: CrewMember[];
  locs: Record<string, LocRow>;
  selfId: string;
  talkingIds?: Set<string>;
};

// Animated equalizer-style bars rendered next to a crew member's name while
// they're holding PTT. Purely CSS keyframes — no JS ticker.
function TalkingBars() {
  return (
    <span className="talking-bars" aria-label="talking">
      <span className="talking-bar" />
      <span className="talking-bar" />
      <span className="talking-bar" />
    </span>
  );
}

// Presence-at-a-glance panel. Floats top-left under the group chip, collapses
// to a chip on tap so it doesn't swallow the map when crews get big.
export function CrewLegend({ crew, locs, selfId, talkingIds }: Props) {
  const [open, setOpen] = useState(true);
  const [, setTick] = useState(0);

  // Re-render every 30s so staleness labels stay honest.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  if (!crew || crew.length === 0) return null;

  const rows = crew
    .map((c) => {
      const loc = locs[c.id];
      const presence = presenceFor(loc, c.share_location);
      return { c, loc, presence };
    })
    .sort((a, b) => {
      const pa = presenceOrder[a.presence];
      const pb = presenceOrder[b.presence];
      if (pa !== pb) return pa - pb;
      if (a.c.id === selfId) return -1;
      if (b.c.id === selfId) return 1;
      return a.c.display_name.localeCompare(b.c.display_name);
    });

  const freshCount = rows.filter((r) => r.presence === 'fresh').length;
  const offlineCount = rows.filter((r) => r.presence === 'offline').length;

  const summary = `${crew.length} · ${freshCount} live${
    offlineCount > 0 ? ` · ${offlineCount} offline` : ''
  }`;

  if (!open) {
    return (
      <button className="legend-chip" onClick={() => setOpen(true)}>
        <span className="legend-dot" />
        {summary}
        <style>{legendCss}</style>
      </button>
    );
  }

  return (
    <div className="legend">
      <button className="legend-head" onClick={() => setOpen(false)}>
        <span>
          {crew.length} member{crew.length === 1 ? '' : 's'} · {freshCount} live
          {offlineCount > 0 ? ` · ${offlineCount} offline` : ''}
        </span>
        <span className="legend-collapse">–</span>
      </button>
      <div className="legend-scroll">
        {rows.map(({ c, loc, presence }) => {
          let label: string;
          let tint: string = colors.textMuted;
          switch (presence) {
            case 'fresh':
              label = stalenessLabel(loc!.updated_at);
              tint = colors.text;
              break;
            case 'stale':
              label = `stale · ${stalenessLabel(loc!.updated_at)}`;
              break;
            case 'offline':
              label = `offline · ${stalenessLabel(loc!.updated_at)}`;
              tint = colors.accentDim;
              break;
            case 'sharing_off':
              label = 'sharing off';
              break;
            case 'no_signal':
            default:
              label = 'no signal';
              break;
          }
          const dotColor =
            presence === 'fresh'
              ? colors.accent
              : presence === 'offline'
              ? colors.accentDim
              : presence === 'stale'
              ? '#7a7a7a'
              : colors.divider;
          return (
            <div key={c.id} className="legend-row">
              <span className="legend-pip" style={{ background: dotColor }} />
              <span className="legend-name">
                {c.display_name}
                {c.id === selfId ? ' (you)' : ''}
              </span>
              {talkingIds?.has(c.id) && <TalkingBars />}
              <span className="legend-meta" style={{ color: tint }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
      <style>{legendCss}</style>
    </div>
  );
}

const legendCss = `
  .legend {
    position: absolute;
    top: calc(env(safe-area-inset-top) + 72px);
    left: 12px;
    width: 230px;
    max-height: 44vh;
    background: rgba(23,22,28,0.92);
    border: 1px solid ${colors.divider};
    border-radius: 10px;
    z-index: 900;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .legend-head {
    background: transparent;
    color: ${colors.textMuted};
    border: none;
    border-bottom: 1px solid ${colors.divider};
    padding: 8px 10px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }
  .legend-collapse { color: ${colors.accent}; font-size: 14px; }
  .legend-scroll { overflow-y: auto; padding: 4px 0; }
  .legend-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    font-size: 13px;
    color: ${colors.text};
    min-width: 0;
  }
  .legend-pip {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
  .legend-name { font-weight: 600; flex-shrink: 0; }
  .legend-meta {
    margin-left: auto;
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .legend-chip {
    position: absolute;
    top: calc(env(safe-area-inset-top) + 72px);
    left: 12px;
    background: rgba(23,22,28,0.92);
    color: ${colors.text};
    border: 1px solid ${colors.divider};
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    z-index: 900;
    cursor: pointer;
  }
  .legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.accent};
  }
  .talking-bars {
    display: inline-flex;
    align-items: flex-end;
    gap: 2px;
    height: 12px;
    margin-left: 6px;
    flex-shrink: 0;
  }
  .talking-bar {
    width: 3px;
    background: ${colors.accent};
    border-radius: 1px;
    animation: talkingBar 700ms ease-in-out infinite;
  }
  .talking-bar:nth-child(1) { animation-delay: 0ms;   height: 6px; }
  .talking-bar:nth-child(2) { animation-delay: 140ms; height: 10px; }
  .talking-bar:nth-child(3) { animation-delay: 280ms; height: 8px; }
  @keyframes talkingBar {
    0%, 100% { transform: scaleY(0.5); }
    50%      { transform: scaleY(1.2); }
  }
`;
